import { Injector } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { HttpClient, HttpParams, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Router } from '@angular/router';
import { CgpAlertDialogService } from './cgp-alert-dialog/cgp-alert-dialog.service';
import { cgpCookieHelper } from '../../../src/modules/cgp-cookie-helper';
import { config } from '../../../src/config/config';
import { cgpAuthentication } from '../../../src/modules/cgp-authentication';

const applicationJson = 'application/json';

export abstract class BaseHttpService extends BehaviorSubject<any> {
    private readonly http: HttpClient;
    private readonly router: Router;
    private readonly cgpAlertDialogService: CgpAlertDialogService;
    loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    filteredData: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
    httpErrorHeader: string;

    constructor(
        private readonly injector: Injector,
    ) {
        super(null);
        this.http = injector.get(HttpClient);
        this.router = injector.get(Router);
        this.cgpAlertDialogService = injector.get(CgpAlertDialogService);

        this.subscribe(x => {
            (x && x.length > 0) ? this.filteredData.next(x) : this.filteredData.next([x]);
            this.loading.next(false);
        });
    }


    /**
     *
     * @param value
     * @param field
     */
    handleFilter = (value: string, field: string): void => {
        if (value) {
            const check: string = value.toString();
            const result = this.value
                .filter(x => {
                    try {
                        const source: string = x[field].toString().toLowerCase();
                        return source.indexOf(check.toLowerCase()) !== -1;
                    } catch (e) {
                        this.filteredData.next(this.value);
                    }
                });
            this.filteredData.next(result);
        } else {
            this.filteredData.next(this.value);
        }
    }


    /**
     *
     */
    handleError = (error: HttpErrorResponse, isShowDialog = false) => {
        this.loading.next(false);
        if (error.headers) { this.updateToken(error.headers); }
        if (isShowDialog) { this.showErrorDialog(error); }
    }


    /**
     *
     * @param error
     */
    private showErrorDialog(error: HttpErrorResponse) {
        const isUnauthorized: boolean = error.status === 401;
        const _unauthMsg = '. Click OK to refresh token';
        const _body = this.getErrorBody(error) + (isUnauthorized ? _unauthMsg : '');
        const _status = `${error.status} ${error.statusText}`;
        this.cgpAlertDialogService.showAlertDialog({
            title: 'Error',
            message: _body,
            alert: 'danger',
            closeLabel: 'OK',
            onClosed: () => isUnauthorized ? this.refreshToken() : null,
        });
    }


    /**
     *
     * @param url
     * @param catchError
     */
    protected fetch(url, catchError = true): Promise<any> {
        const options = this.getOptions();
        const response = this.http.get(url, options);
        return this.handleResponse(response, catchError);
    }


    /**
     *
     * @param body
     * @param url
     * @param catchError
     */
    protected post(body: any, url: string, catchError = true, searchParams: HttpParams = null): Promise<any> {
        const options = { headers: this.getHeaders(), params: searchParams, observe: 'response' } as any;
        return this.handleResponse(this.http.post(url, JSON.stringify(body), options), catchError);
    }

    protected post_excel(body: any, url: string, catchError = true, searchParams: HttpParams = null): Promise<any> {
        const options = { headers: this.getExcelHeaders(), params: searchParams, observe: 'response' } as any;
        return this.handleResponse(this.http.post(url, body, options), catchError);
    }

    /**
     *
     * @param body
     * @param url
     * @param catchError
     */
    protected put(body: any, url: string, catchError = true, observe = true): Promise<any> {
        const options = this.getOptions();
        if (!observe) { delete options.observe; }
        return this.handleResponse(this.http.put(url, JSON.stringify(body), options), catchError);
    }


    /**
     *
     * @param url
     * @param catchError
     */
    protected delete(url: string, catchError = true): Promise<any> {
        return this.handleResponse(this.http.delete(url, this.getOptions()), catchError);
    }


    /**
     *
     * @param url
     */
    protected download(body: any, url: string, searchParams: HttpParams = null, catchError = true): Promise<any> {
        const headers = this.getHeaders();
        const options = {
            headers,
            params: searchParams,
            observe: 'response',
            responseType: 'arraybuffer'
        } as any;

        return this.handleResponse(this.http.post(url, JSON.stringify(body), options), catchError);
    }

    /**
     *
     * @param url
     */
    protected documentDownload(url: string, searchParams: HttpParams = null, catchError = true): Promise<any> {
        const headers = this.getDownloadHeaders();
        const options = {
            headers,
            params: searchParams,
            observe: 'response',
            responseType: 'blob'
        } as any;

        return this.handleResponse(this.http.get(url, options), catchError);
    }


    /**
     *
     */
    private getHeaders() {
        try {
            this.loading.next(true);
            const token: string = this.getToken();

            return new HttpHeaders({ 'Content-Type': applicationJson })
                .set('Accept', applicationJson)
                .set('Authorization', token);
        } catch (e) { }
    }


    /**
     *
     */
    private getExcelHeaders() {
        try {
            this.loading.next(true);
            const token: string = this.getToken();

            return new HttpHeaders()
                .set('Accept', applicationJson)
                .set('Authorization', token);
        } catch (e) { }
    }

    /**
 *
 */
    private getDownloadHeaders() {
        try {
            this.loading.next(true);
            const token: string = this.getToken();

            return new HttpHeaders()
                .set('Accept', 'application/octet-stream')
                .set('Authorization', token);
        } catch (e) { }
    }


    /**
     *
     */
    private getOptions(): any {
        const headers = this.getHeaders();
        return { headers, observe: 'response' } as any;
    }


    /**
     *
     */
    private getToken(): string {
        const _token = cgpCookieHelper.retrieveCookie(config.authentication.authenticationCookie);
        return _token.replace('Bearer_', 'Bearer ');
    }


    /**
     *
     * @param headers
     */
    private updateToken(headers: any) {
        try {
            let newToken = headers.get(config.authentication.authenticationCookie);
            newToken = newToken.replace('Bearer ', '');
            cgpCookieHelper.setAuthCookie(newToken);
        } catch (e) { }
    }


    /**
     *
     */
    private readonly refreshToken = () => {
        this.router.navigate(['/']);
        cgpAuthentication.refreshToken(null);
        window.location.reload();
    }


    /**
     *
     * @param response
     * @param catchError
     */
    private handleResponse(response: Observable<any>, catchError = true): Promise<any> {
        const responsePromise: Promise<any> = response
            .toPromise()
            .then(x => {
                this.updateToken(x.headers);
                if (x && x.body) {
                    this.next(x.body);
                    return x.body;
                } else {
                    this.loading.next(false);
                    return x;
                }
            });
        return catchError ? responsePromise.catch(e => this.handleError(e, true)) : responsePromise;
    }


    /**
     *
     * @param error
     */
    private getErrorBody(error: any): string {
        const _err = error.error ? error.error : error;
        if (typeof (_err) === typeof ({})) {
            const errors: any[] = [];
            Object.keys(_err).forEach(k => {
                const e = _err[k].Errors;
                if (e && e[0]) {
                    errors.push(e[0].ErrorMessage);
                }
            });
            return errors.join('\n');
        }
        return _err;
    }

}
